import {
  ArrowLeft,
  Captions,
  Check,
  Copy,
  FileText,
  Loader2,
  MessageSquare,
  Trash2,
  Video,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CopyLinkButton, meetRoomPath } from '../components/meet/MeetingCard';
import { useCallDuration } from '../components/teamChat/VoiceCallHistory';
import { LiveBadge } from '../components/teamChat/VoiceChannelRow';
import { transcriptToText, VoiceTranscript } from '../components/teamChat/VoiceTranscript';
import { Avatar } from '../components/tasks/linear/Avatar';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { useI18n } from '../contexts/I18nContext';
import { useDeleteMeeting, useMeeting, useMeetingChat, useWriteMeetingLog } from '../hooks/useMeet';
import { MeetChatMessages } from '../components/meet/MeetChatPanel';
import { MeetRecordingsSection } from '../components/meet/MeetRecordingsSection';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../store/useAuthStore';

const HEADER_BUTTON =
  'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors disabled:opacity-50';

const whenFormat = new Intl.DateTimeFormat('ro-RO', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * /meet/history/:id — one meeting after the fact: who was there, how long,
 * everything that was said, what was typed in the chat, and the meetings-log
 * entry it became (with the AI summary and action items). For a meeting
 * still running it links back to the room instead.
 */
export function MeetHistoryPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { id = '' } = useParams<{ id: string }>();
  const me = useAuthStore((s) => s.user);
  const { can } = usePermissions();
  const duration = useCallDuration();
  const { data: meeting, isLoading } = useMeeting(id || undefined);
  const { data: chat } = useMeetingChat(id || undefined);
  const remove = useDeleteMeeting();
  const writeLog = useWriteMeetingLog();
  const [copied, setCopied] = useState(false);

  if (!meeting) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent">
          {isLoading ? (
            <Loader2 size={26} className="animate-spin text-muted-foreground" />
          ) : (
            <Video size={26} className="text-muted-foreground" />
          )}
        </span>
        {!isLoading && (
          <>
            <p className="max-w-sm text-sm text-muted-foreground">{t('meet.notFound')}</p>
            <Link to="/meet" className="text-sm font-medium text-primary hover:underline">
              {t('meet.back')}
            </Link>
          </>
        )}
      </div>
    );
  }

  const live = meeting.status === 'LIVE' || meeting.status === 'SCHEDULED';
  const isHost = meeting.hostId === me?.id;
  const admitted = meeting.participants.filter((p) => p.admission === 'ADMITTED');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(transcriptToText(meeting.segments));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked; the text is on screen to select.
    }
  };

  const del = async () => {
    const yes = await confirm({
      title: t('meet.delete'),
      message: t('meet.confirmDelete', { title: meeting.title }),
      confirmLabel: t('common.delete'),
      danger: true,
    });
    if (!yes) return;
    remove.mutate(meeting.id, { onSuccess: () => navigate('/meet') });
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <Link
          to="/meet"
          title={t('meet.back')}
          className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <ArrowLeft size={18} />
        </Link>
        <Video
          size={18}
          className={
            meeting.status === 'LIVE'
              ? 'shrink-0 text-emerald-500'
              : 'shrink-0 text-muted-foreground'
          }
        />
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 truncate font-heading text-base font-semibold text-foreground">
            <span className="truncate">{meeting.title}</span>
            {meeting.status === 'LIVE' && <LiveBadge />}
          </h1>
          <p className="truncate text-xs text-muted-foreground">
            {whenFormat.format(new Date(meeting.startedAt ?? meeting.scheduledAt))}
            {meeting.durationMs > 0 && ` · ${duration(meeting.durationMs)}`}
            {' · '}
            {t('meet.hostedBy', { name: meeting.hostName })}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {live ? (
            <>
              <CopyLinkButton url={meeting.joinUrl} compact />
              <button
                type="button"
                onClick={() => navigate(meetRoomPath(meeting.code))}
                className={`${HEADER_BUTTON} bg-emerald-600 text-fixed-white hover:bg-emerald-700`}
              >
                {t('meet.join')}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => void copy()}
              disabled={meeting.segments.length === 0}
              title={t('teamChat.voiceCallCopy')}
              className={`${HEADER_BUTTON} text-muted-foreground hover:bg-accent hover:text-foreground`}
            >
              {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
              <span className="hidden sm:inline">
                {copied ? t('teamChat.voiceCallCopied') : t('teamChat.voiceCallCopy')}
              </span>
            </button>
          )}
          {can('videoMeetings', 'canManage') && (
            <button
              type="button"
              onClick={() => void del()}
              disabled={remove.isPending}
              title={t('meet.delete')}
              className={`${HEADER_BUTTON} text-red-600 hover:bg-red-500/15`}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
        {/* Where the transcript went: the meetings log, with the AI summary. */}
        {meeting.status === 'ENDED' && (
          <section className="rounded-lg border border-border bg-card px-4 py-3">
            {meeting.logMeetingId ? (
              <div className="flex flex-wrap items-center gap-3">
                <FileText size={18} className="shrink-0 text-muted-foreground" />
                <p className="min-w-0 flex-1 text-sm text-foreground">{t('meet.log.written')}</p>
                <Link
                  to={`/meetings/${encodeURIComponent(meeting.logMeetingId)}`}
                  className="rounded-md bg-foreground px-3 py-1.5 text-sm font-medium text-background hover:bg-foreground/90"
                >
                  {t('meet.log.open')}
                </Link>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <FileText size={18} className="shrink-0 text-muted-foreground" />
                <p className="min-w-0 flex-1 text-sm text-muted-foreground">
                  {meeting.logProcessedAt ? t('meet.log.skipped') : t('meet.log.pending')}
                </p>
                {(isHost || can('videoMeetings', 'canManage')) && (
                  <button
                    type="button"
                    onClick={() => writeLog.mutate(meeting.id)}
                    disabled={writeLog.isPending}
                    className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50"
                  >
                    {writeLog.isPending ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      t('meet.log.create')
                    )}
                  </button>
                )}
              </div>
            )}
          </section>
        )}

        <section>
          <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {t('teamChat.voiceCallParticipants')}
          </h2>
          {admitted.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('meet.noParticipants')}</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {admitted.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center gap-2 rounded-full border border-border bg-card py-1 pl-1 pr-3 text-sm"
                >
                  <Avatar name={p.displayName} size={22} />
                  <span className="text-foreground">
                    {p.userId === me?.id ? t('meet.you') : p.displayName}
                  </span>
                  {p.guest && (
                    <span className="rounded bg-accent px-1.5 text-[10px] font-medium uppercase text-muted-foreground">
                      {t('meet.guest')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h2 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            <Captions size={14} />
            {t('teamChat.voiceTranscript')}
          </h2>
          <div className="rounded-lg border border-border bg-card px-4 py-3">
            <VoiceTranscript
              segments={meeting.segments}
              meId={me?.id}
              emptyText={
                live ? t('meet.transcript.liveElsewhere') : t('teamChat.voiceCallNoTranscript')
              }
            />
          </div>
        </section>

        {/* What was typed during the call; kept with the meeting. Shown only
            once something was written: an empty chat is not news. */}
        {chat && chat.length > 0 && (
          <section>
            <h2 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              <MessageSquare size={14} />
              {t('meet.chat.title')}
            </h2>
            <div className="rounded-lg border border-border bg-card px-4 py-3">
              <MeetChatMessages messages={chat} meId={me?.id} emptyText={t('meet.chat.none')} />
            </div>
          </section>
        )}

        <MeetRecordingsSection meetingId={meeting.id} title={meeting.title} />
      </div>
    </div>
  );
}
